import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  useCancelRegistration,
  useFlag,
  useFlags,
  useFreezeToastAck,
  useKaliningrad,
  usePassFreezes,
  useQualification,
  useQualificationArchive,
  useRegister,
} from '../api/hooks';
import { KaliningradBoard, kgdSegmentLabel } from '../components/qualification/Kaliningrad';
import { ErrorView } from '../components/StateViews';
import { haptics } from '../telegram';
import { SheetClose } from '../components/SheetClose';
import { CosmeticAvatar } from '../components/cosmetics/CosmeticAvatar';
import { NickFx } from '../components/cosmetics/NickFx';
import { RowFxLayer, rowFxClass } from '../components/cosmetics/RowFx';
import { SnowIcon } from './PassScreen';
import { track } from '../analytics';
import { shortDate, timeLabel, plural } from '../format';
import {
  CalibIcon,
  DecayIcon,
  FloorIcon,
  InfoIcon,
  TicketIcon,
  WarnIcon,
  ZoneIcon,
} from '../components/qualification/icons';
import logoUrl from '../assets/straddle-logo.svg';
import type { QualificationOverview, QualRankedRow } from '../api/types';

function fmtDayTime(iso: string): string {
  const d = new Date(iso);
  const wd = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'][d.getDay()];
  return `${wd} ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} · ${timeLabel(iso)}`;
}

const STATE_META: Record<string, { Icon: (p: { size?: number; className?: string }) => JSX.Element; word: string; cls: string } | undefined> = {
  warning: { Icon: WarnIcon, word: 'пропуски', cls: 'q-st-warn' },
  decaying: { Icon: DecayIcon, word: 'затухание', cls: 'q-st-decay' },
  at_floor: { Icon: FloorIcon, word: 'старт', cls: 'q-st-floor' },
};

/**
 * Вкладка «Отбор». За флагом `kaliningrad` — два параллельных отбора под
 * сегмент-контролом (design_handoff_kaliningrad_v2); без флага — ровно прежний
 * экран Сочи.
 */
export function QualificationScreen() {
  const kalOn = useFlag('kaliningrad');
  if (!kalOn) return <SochiQualification />;
  return <SegmentedQualifications />;
}

/** Прежний экран Elo-отбора (Сочи) — не меняется. */
function SochiQualification() {
  const q = useQualification();

  if (q.isLoading) return <QualSkeleton />;
  if (q.isError) return <ErrorView message={(q.error as Error)?.message} />;
  const data = q.data;
  if (!data || data.status === 'none') return <BetweenState overview={null} />;
  if (data.status === 'upcoming') return <BetweenState overview={data} />;

  return <ActiveQualification data={data} />;
}

type QualSegment = 'kaliningrad' | 'sochi';

function readSegment(): QualSegment | null {
  try {
    const v = localStorage.getItem('qual.segment');
    return v === 'kaliningrad' || v === 'sochi' ? v : null;
  } catch {
    return null;
  }
}

/** Сегмент-контрол «Калининград / Сочи» + выбранный лидерборд. */
function SegmentedQualifications() {
  const kal = useKaliningrad(true);
  const sochi = useQualification();
  const [chosen, setChosen] = useState<QualSegment | null>(() => readSegment());

  if (kal.isLoading) return <QualSkeleton />;
  const kalData = kal.data;
  // Пул-отбора нет (или ручка недоступна) — показываем прежний экран без сегмента.
  if (kal.isError || !kalData || kalData.status === 'none') return <SochiQualification />;

  // Дефолт — ближайший по дате конца отбор; выбор запоминается локально.
  const defaultSeg: QualSegment = (() => {
    const kEnd = kalData.event ? new Date(kalData.event.endsAt).getTime() : Infinity;
    const sEnd = sochi.data?.qualification
      ? new Date(sochi.data.qualification.endsAt).getTime()
      : Infinity;
    const now = Date.now();
    const kd = kEnd >= now ? kEnd - now : Infinity;
    const sd = sEnd >= now ? sEnd - now : Infinity;
    return sd < kd ? 'sochi' : 'kaliningrad';
  })();
  const seg = chosen ?? defaultSeg;
  const pick = (s: QualSegment) => {
    setChosen(s);
    try {
      localStorage.setItem('qual.segment', s);
    } catch {
      /* приватный режим */
    }
    track('qual_segment', { segment: s });
  };

  const sochiSub = (() => {
    const d = sochi.data;
    if (!d || d.status === 'none') return '';
    if (d.status === 'upcoming') return 'скоро';
    if (d.status === 'finished') return 'завершён';
    const p = d.progress;
    return p ? `игра ${p.gamesPlayedTotal} из ${p.plannedGames}` : '';
  })();

  return (
    <div className="kq-screen">
      <div className="kq-seg">
        <button className={seg === 'kaliningrad' ? 'active' : ''} onClick={() => pick('kaliningrad')}>
          <span className="nm">Калининград</span>
          <span className="sub mono">{kgdSegmentLabel(kalData)}</span>
        </button>
        <button className={seg === 'sochi' ? 'active' : ''} onClick={() => pick('sochi')}>
          <span className="nm">Сочи</span>
          <span className="sub mono">{sochiSub}</span>
        </button>
      </div>
      {seg === 'kaliningrad' ? (
        <KaliningradBoard data={kalData} />
      ) : (
        <div className="kq-sochi-wrap">
          <SochiQualification />
        </div>
      )}
    </div>
  );
}

/** Шестигранник вечной заморозки (5.3/6.9): лёд + золото. */
function EternalHexIcon({ size = 11 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden>
      <defs>
        <linearGradient id="q-ehex" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9FE3FA" />
          <stop offset=".55" stopColor="#E9F7FF" />
          <stop offset="1" stopColor="#C8A04E" />
        </linearGradient>
      </defs>
      <path d="M24 2.5 42.6 13.25v21.5L24 45.5 5.4 34.75v-21.5z" fill="#0A1A33" stroke="url(#q-ehex)" strokeWidth="4" />
      <path d="M24 12v8M24 28v8M14 17.5l7 4M27 26.5l7 4M14 30.5l7-4M27 21.5l7-4" stroke="#9FE3FA" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** 5.2 — тост «Заморозка спасла твои баллы за пропуск» (вотермарк на тратах). */
function FreezeSavedToast() {
  const flags = useFlags();
  const freezes = usePassFreezes(Boolean(flags.data?.pass));
  const ack = useFreezeToastAck();
  const [toast, setToast] = useState<{ gameAt: string | null; left: number } | null>(null);
  const caught = useState(() => ({ done: false }))[0];
  useEffect(() => {
    const t = freezes.data?.savedToast ?? null;
    if (!t || caught.done) return;
    caught.done = true;
    setToast({ gameAt: t.lastGameAt, left: t.left });
    haptics.light();
    ack.mutate();
  }, [freezes.data?.savedToast]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!toast) return null;
  // макет 5.2: день недели вместо даты, «Осталось: N игры», без «×»
  const WD = ['в воскресенье', 'в понедельник', 'во вторник', 'в среду', 'в четверг', 'в пятницу', 'в субботу'];
  return (
    <div className="q-freeze-toast" onClick={() => setToast(null)} role="button">
      <span className="ic">
        <SnowIcon size={18} />
      </span>
      <span className="tx">
        <b>
          Заморозка спасла твои баллы за пропуск
          {toast.gameAt ? ` ${WD[new Date(toast.gameAt).getDay()]}` : ''}
        </b>
        <span>
          Осталось: {toast.left} {toast.left === 1 ? 'игра' : toast.left < 5 ? 'игры' : 'игр'}
        </span>
      </span>
    </div>
  );
}

function ActiveQualification({ data }: { data: QualificationOverview }) {
  const [rulesOpen, setRulesOpen] = useState(false);
  // 5.1: вход на экран заморозок — вкладка «Отбор» (макет)
  const passEnabled = Boolean(useFlags().data?.pass);
  const freezes = usePassFreezes(passEnabled);
  const freezesOn = freezes.data?.status === 'active' && Boolean(freezes.data.freezesEnabled);
  const fb = freezes.data?.balances ?? { game1: 0, game3: 0, week: 0 };
  const cfg = data.qualification!;
  const progress = data.progress!;
  const finished = data.status === 'finished';
  const tickets = data.tickets ?? [];
  const ranked = data.ranked ?? [];
  const calibrating = data.calibrating ?? [];
  const me = data.me ?? null;
  const nearMiss = ranked.filter((r) => r.rank > cfg.tickets && r.rank <= cfg.tickets + 2);
  const rest = ranked.filter((r) => r.rank > cfg.tickets + 2);
  const meRow = ranked.find((r) => r.isMe) ?? null;
  const firstWeek = !finished && ranked.length === 0;

  return (
    <div className="q-screen">
      <Header cfg={cfg} progress={progress} data={data} finished={finished} onRules={() => setRulesOpen(true)} />
      <FreezeSavedToast />

      {freezesOn && (
        <Link className="bpf-entry" to="/pass/freezes">
          <SnowIcon size={14} />
          <span className="nm">Заморозки</span>
          <span className="cnt mono">
            {freezes.data?.eternal
              ? '∞'
              : fb.game1 + fb.game3 + fb.week > 0
                ? `×${fb.game1 + fb.game3 + fb.week}`
                : ''}
          </span>
          <span className="chev">›</span>
        </Link>
      )}

      {finished && tickets.length > 0 && (
        <div className="q-results-banner">
          Итоги отбора · Поздравляем: {tickets.map((t) => t.name).join(', ')}!
        </div>
      )}

      <YouNow me={me} cfg={cfg} finished={finished} />

      <div className="q-overline">
        <TicketIcon size={11} /> ЕДУТ НА ЧЕМПИОНАТ
      </div>
      <div className="q-tickets">
        {Array.from({ length: cfg.tickets }, (_, i) => {
          const t = tickets[i];
          return t ? (
            <TicketRow key={t.playerId} t={t} place={i + 1} finished={finished} tickets={cfg.tickets} />
          ) : (
            <div key={`empty-${i}`} className="q-ticket q-ticket--empty">
              <div className="q-ticket-stub">
                <div className="num">#{i + 1}</div>
                <div className="lbl">МЕСТО</div>
              </div>
              <div className="q-ticket-body">
                <span className="q-ticket-empty-ico">
                  <TicketIcon size={20} />
                </span>
                <span className="q-ticket-empty-text">Место свободно</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className={`q-cutoff ${progress.finalWeek && !finished ? 'q-cutoff--final' : ''}`}>
        <span className="q-cutoff-pill">
          <ZoneIcon size={10} /> ЗОНА ОТБОРА
        </span>
      </div>
      {!firstWeek && <div className="q-cutoff-note">#{cfg.tickets + 1} и #{cfg.tickets + 2} — в шаге от отбора</div>}

      {firstWeek ? (
        <div className="q-calib-block">
          <div className="q-calib-head">
            <CalibIcon size={14} /> В калибровке — все
          </div>
          <div className="q-calib-sub">
            Сыграй {cfg.calGames} турнира — и займёшь место в таблице. Первые игры меняют рейтинг сильнее.
          </div>
          <CalibList rows={calibrating} />
        </div>
      ) : (
        <>
          <div className="q-table">
            {nearMiss.map((r) => (
              <Row key={r.playerId} r={r} near finished={finished} finalWeek={progress.finalWeek} cutoff={tickets[cfg.tickets - 1]?.rating ?? null} />
            ))}
            {rest.map((r) => (
              <Row key={r.playerId} r={r} finished={finished} finalWeek={progress.finalWeek} cutoff={null} />
            ))}
          </div>
          {finished && data.frozenAt && (
            <div className="q-frozen-note">Таблица заморожена {shortDate(data.frozenAt)}</div>
          )}
          {calibrating.length > 0 && !finished && (
            <div className="q-calib-block">
              <div className="q-calib-head">
                <CalibIcon size={14} /> В калибровке — ещё не в зачёте
              </div>
              <div className="q-calib-sub">
                Место появится после {cfg.calGames}-го турнира. Рейтинг пока меняется сильнее.
              </div>
              <CalibList rows={calibrating} />
            </div>
          )}
        </>
      )}

      {finished && me && (
        <Link to="/qualification/me" className="q-final-me">
          <div className="row">
            <div>
              <div className="v mono">{me.rank !== null ? `#${me.rank}` : '—'}</div>
              <div className="l">твоё место</div>
            </div>
            <div>
              <div className="v mono">{me.rating}</div>
              <div className="l">рейтинг</div>
            </div>
            <div>
              <div className="v mono">{me.gamesPlayed}</div>
              <div className="l">турниров</div>
            </div>
          </div>
          <div className="q-final-next">Следующий отбор — скоро объявим</div>
        </Link>
      )}

      {meRow && !finished && (
        <div className="me-sticky" key={`qme-${meRow.rank}`}>
          <div className={`q-row q-row--me q-row--sticky q-row--pinned${rowFxClass(meRow.cosmetics?.row)}`}>
            <RowFxLayer cosmetic={meRow.cosmetics?.row} height={52} />
            <RowInner r={{ ...meRow, name: `Вы · ${meRow.name}` }} />
          </div>
        </div>
      )}

      {rulesOpen && <RulesSheet cfg={cfg} onClose={() => setRulesOpen(false)} />}
    </div>
  );
}

function Header({
  cfg,
  progress,
  data,
  finished,
  onRules,
}: {
  cfg: NonNullable<QualificationOverview['qualification']>;
  progress: NonNullable<QualificationOverview['progress']>;
  data: QualificationOverview;
  finished: boolean;
  onRules: () => void;
}) {
  const t = data.nextTournament ?? null;
  const register = useRegister(t?.id ?? 0);
  const cancel = useCancelRegistration(t?.id ?? 0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Доля сыгранных турниров текущей недели — частичная заливка сегмента.
  const perWeek = progress.plannedGames / progress.weeksTotal;
  const inWeek = Math.max(0, progress.gamesPlayedTotal - progress.weekNo * perWeek);
  const currentFrac = Math.min(1, perWeek > 0 ? inWeek / perWeek : 0);

  return (
    <div className="q-header" data-theme="dark">
      <img src={logoUrl} alt="" className="q-header-wm" />
      <div className="q-header-top">
        <span className="q-header-label">
          <TicketIcon size={12} /> ОТБОР
        </span>
        <button
          className="q-header-rules"
          onClick={() => {
            haptics.tap();
            track('qualification_rules_open', {});
            onRules();
          }}
        >
          <InfoIcon size={12} /> Как считается рейтинг
        </button>
      </div>
      <div className="q-header-title">{cfg.name}</div>

      <div className="q-week-segments">
        {Array.from({ length: progress.weeksTotal }, (_, i) => (
          <span key={i} className="q-seg">
            <i
              style={{
                width:
                  i < progress.weekNo || finished
                    ? '100%'
                    : i === progress.weekNo
                      ? `${Math.round(currentFrac * 100)}%`
                      : '0%',
              }}
            />
          </span>
        ))}
      </div>
      <div className="q-week-line">
        {finished ? (
          <span>Отбор завершён · сыграно {progress.gamesPlayedTotal} турниров</span>
        ) : progress.finalWeek ? (
          <span className="q-final-label">
            Финал · осталось {Math.min(progress.remainingGames.length, Math.max(0, progress.plannedGames - progress.gamesPlayedTotal))}{' '}
            {plural(Math.min(progress.remainingGames.length, Math.max(1, progress.plannedGames - progress.gamesPlayedTotal)), ['турнир', 'турнира', 'турниров'])}
          </span>
        ) : (
          <span>
            Неделя {progress.weekNo + 1} из {progress.weeksTotal} · сыграно {progress.gamesPlayedTotal} из{' '}
            {progress.plannedGames}
          </span>
        )}
        {!finished && <span className="q-days mono">ещё {progress.daysLeft} {plural(progress.daysLeft, ['день', 'дня', 'дней'])}</span>}
      </div>

      {progress.finalWeek && !finished && progress.remainingGames.length > 0 && (
        <div className="q-final-chips">
          {progress.remainingGames.slice(0, 3).map((g) => (
            <Link key={g.id} to={`/tournaments/${g.id}`} className="q-final-chip mono">
              {shortDate(g.startsAt)}
            </Link>
          ))}
        </div>
      )}

      {t && !finished && (
        <div className="q-next">
          <div className="q-next-info">
            <div className="q-next-when">{fmtDayTime(t.startTime)}</div>
            <div className="q-next-count">{t.registeredCount} записан{t.registeredCount % 10 === 1 && t.registeredCount % 100 !== 11 ? '' : 'о'}</div>
          </div>
          {t.registeredByMe ? (
            <button
              className="q-next-btn q-next-btn--in"
              onClick={() => {
                haptics.tap();
                void cancel.mutateAsync().catch(() => undefined);
              }}
            >
              ✓ Ты записан
            </button>
          ) : (
            <button
              className="q-next-btn q-next-btn--go"
              onClick={() => {
                haptics.tap();
                setConfirmOpen(true);
              }}
            >
              Записаться
            </button>
          )}
        </div>
      )}

      {confirmOpen && t && (
        <div className="sheet-backdrop" onClick={() => setConfirmOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <SheetClose onClose={() => setConfirmOpen(false)} />
            <h3>Запись на турнир отбора</h3>
            <div className="sheet-sub">
              {t.title ?? t.format ?? 'Турнир'} · {fmtDayTime(t.startTime)}
            </div>
            <div className="sheet-row">
              <span className="l">За участие</span>
              <span className="v" style={{ color: 'var(--green)' }}>+{cfg.bonus} к баллам отбора</span>
            </div>
            <button
              className="btn"
              style={{ width: '100%', marginTop: 12 }}
              disabled={register.isPending}
              onClick={() => {
                track('qualification_register', { tournamentId: t.id });
                void register
                  .mutateAsync()
                  .then(() => {
                    haptics.success();
                    setConfirmOpen(false);
                  })
                  .catch(() => haptics.error());
              }}
            >
              {register.isPending ? 'Запись…' : 'Записаться'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function YouNow({
  me,
  cfg,
  finished,
}: {
  me: QualificationOverview['me'] & object | null;
  cfg: NonNullable<QualificationOverview['qualification']>;
  finished: boolean;
}) {
  const navigate = useNavigate();
  if (finished) return null;
  return (
    <button
      className="q-younow"
      onClick={() => {
        haptics.tap();
        navigate('/qualification/me');
      }}
    >
      <div className="q-younow-main">
        <div className="q-younow-left">
          <div className="q-lbl">ТЫ СЕЙЧАС</div>
          <div className="q-younow-rank mono">
            {me === null || me.rank === null ? '—' : `#${me.rank}`}
            {me && me.rankChangeWeek !== null && me.rankChangeWeek !== 0 && (
              <span className={`q-shift ${me.rankChangeWeek > 0 ? 'up' : 'dn'}`}>
                {me.rankChangeWeek > 0 ? `▲${me.rankChangeWeek}` : `▼${-me.rankChangeWeek}`}
              </span>
            )}
          </div>
        </div>
        <div className="q-younow-right">
          {me && (
            <>
              <div className="q-younow-rating mono">
                {me.frozen && (
                  <span className="q-frost-badge" title="Заморозка активна">
                    <SnowIcon size={10} />
                  </span>
                )}
                {me.rating}
              </div>
              <div className="q-younow-week">
                {me.frozen
                  ? '±0 за пропуск'
                  : `${me.weekDelta >= 0 ? `+${me.weekDelta}` : me.weekDelta} за неделю`}
              </div>
            </>
          )}
          <span className="q-chev">›</span>
        </div>
      </div>
      <div className="q-younow-sub">
        {me === null ? (
          <>Сыграй первый турнир отбора — все стартуют с {cfg.startRating}</>
        ) : me.rank !== null && me.rank <= cfg.tickets ? (
          <>
            Ты в зоне отбора · отрыв от #{cfg.tickets + 1} —{' '}
            <b className="mono">{me.gapToTop3 !== null ? -me.gapToTop3 : '—'}</b>
          </>
        ) : me.state === 'calibrating' ? (
          <>
            Ещё {me.calGamesLeft} {plural(me.calGamesLeft, ['игра', 'игры', 'игр'])} до зачёта
            {me.rankIfQualified !== null && <> · сейчас был бы #{me.rankIfQualified}</>}
          </>
        ) : (
          <>
            До #{cfg.tickets} — <b className="q-gap mono">{me.gapToTop3 ?? '—'}</b>{' '}
            {me.gapToTop3 !== null && me.gapToTop3 <= 25 && '≈ один хороший вечер'}
          </>
        )}
      </div>
    </button>
  );
}

function TicketRow({
  t,
  place,
  finished,
  tickets,
}: {
  t: NonNullable<QualificationOverview['tickets']>[number];
  place: number;
  finished: boolean;
  tickets: number;
}) {
  return (
    <Link
      to={`/qualification/players/${t.playerId}`}
      className={`q-ticket ${place === 1 ? 'q-ticket--first' : ''} ${t.isMe ? 'q-ticket--me' : ''}${rowFxClass(t.cosmetics?.row)}`}
      onClick={() => track('qualification_player_open', { playerId: t.playerId, from: 'ticket' })}
    >
      <RowFxLayer cosmetic={t.cosmetics?.row} height={64} />
      <div className="q-ticket-stub">
        <div className="num mono">#{place}</div>
        <div className="lbl">МЕСТО</div>
      </div>
      <div className="q-ticket-body">
        <span className="q-ticket-ava">
          <CosmeticAvatar name={t.name} src={t.avatarUrl} size={place === 1 ? 34 : 30} frame={t.cosmetics?.frame} />
        </span>
        <div className="q-ticket-who">
          <div className="nm">
            <NickFx cosmetic={t.cosmetics?.nick}>{t.name}</NickFx>
            {t.eternal && (
              <span className="q-eternal-badge" title="Вечная заморозка — одна на клуб">
                <EternalHexIcon size={11} />
              </span>
            )}
          </div>
          <div className="wk mono">
            {t.weekDelta >= 0 ? `↑${t.weekDelta}` : `↓${-t.weekDelta}`} нед.
          </div>
        </div>
        <div className="q-ticket-right">
          <div className="rt mono">{t.rating}</div>
          {finished ? (
            <div className="gap q-gap">Едет на Чемпионат</div>
          ) : t.gapToNext !== null ? (
            <div className={`gap mono ${place === tickets ? 'q-gap' : ''}`}>
              +{t.gapToNext} до #{place === tickets ? tickets + 1 : place + 1}
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function RowInner({ r }: { r: QualRankedRow }) {
  const st = STATE_META[r.state];
  return (
    <>
      <div className="q-rank">
        <div className="n mono">{r.rank}</div>
        <div className={`sh mono ${r.rankChangeWeek === null || r.rankChangeWeek === 0 ? '' : r.rankChangeWeek > 0 ? 'up' : 'dn'}`}>
          {r.rankChangeWeek === null || r.rankChangeWeek === 0
            ? '—'
            : r.rankChangeWeek > 0
              ? `▲${r.rankChangeWeek}`
              : `▼${-r.rankChangeWeek}`}
        </div>
      </div>
      <CosmeticAvatar name={r.name} src={r.avatarUrl} size={30} frame={r.cosmetics?.frame} />
      <div className="q-who">
        <div className="nm">
          <NickFx cosmetic={r.cosmetics?.nick}>{r.name}</NickFx>
          {/* 5.2: иконка заморозки в круге справа от ника */}
          {r.eternal ? (
            <span className="q-eternal-badge" title="Вечная заморозка — одна на клуб">
              <EternalHexIcon size={11} />
            </span>
          ) : r.frozen ? (
            <span className="q-frost-badge" title="Заморозка активна">
              <SnowIcon size={10} />
            </span>
          ) : null}
        </div>
        <div className="games">
          {r.gamesPlayed} игр
          {st && (
            <span className={`q-st ${st.cls}`}>
              {' '}
              · <st.Icon size={10} /> {st.word}
            </span>
          )}
        </div>
      </div>
      <div className="q-right">
        <div className="rt mono">{r.rating}</div>
        {r.lastGameDelta !== null && (
          <div className={`d mono ${r.lastGameDelta > 0 ? 'up' : ''}`}>
            {r.lastGameDelta >= 0 ? `+${r.lastGameDelta}` : r.lastGameDelta}
          </div>
        )}
      </div>
    </>
  );
}

function Row({
  r,
  near = false,
  finished,
  finalWeek,
  cutoff,
}: {
  r: QualRankedRow;
  near?: boolean;
  finished: boolean;
  finalWeek: boolean;
  cutoff: number | null;
}) {
  return (
    <Link
      to={`/qualification/players/${r.playerId}`}
      className={`q-row ${near ? 'q-row--near' : ''} ${r.isMe ? 'q-row--me' : ''}${rowFxClass(r.cosmetics?.row)}`}
      onClick={() => track('qualification_player_open', { playerId: r.playerId, from: 'table' })}
    >
      <RowFxLayer cosmetic={r.cosmetics?.row} height={52} />
      <RowInner r={r} />
      {finalWeek && !finished && near && cutoff !== null && (
        <div className="q-row-finalgap mono">−{Math.max(0, cutoff - r.rating)} до #3</div>
      )}
    </Link>
  );
}

function CalibList({ rows }: { rows: NonNullable<QualificationOverview['calibrating']> }) {
  return (
    <div className="q-calib-list">
      {rows.map((r) => (
        <div key={r.playerId} className={`q-calib-row ${r.isMe ? 'q-row--me' : ''}${rowFxClass(r.cosmetics?.row)}`}>
          <RowFxLayer cosmetic={r.cosmetics?.row} height={44} />
          <span className="q-calib-ico">
            <CalibIcon size={14} />
          </span>
          <CosmeticAvatar name={r.name} src={r.avatarUrl} size={26} frame={r.cosmetics?.frame} />
          <div className="q-calib-who">
            <div className="nm">
              <NickFx cosmetic={r.cosmetics?.nick}>{r.name}</NickFx>
              {r.eternal && (
                <span className="q-eternal-badge" title="Вечная заморозка — одна на клуб">
                  <EternalHexIcon size={10} />
                </span>
              )}
            </div>
            <div className="prog">
              <span className="segs">
                {Array.from({ length: r.calGames }, (_, i) => (
                  <i key={i} className={i < r.gamesPlayed ? 'on' : ''} />
                ))}
              </span>
              <span className="mono">
                {r.gamesPlayed}/{r.calGames}
                {r.rankIfQualified !== null && <> · был бы #{r.rankIfQualified}</>}
              </span>
            </div>
          </div>
          <div className="rt mono">{r.rating}</div>
        </div>
      ))}
    </div>
  );
}

/** Шторка «Как считается рейтинг» — 6 тезисов с ключ-чипами. */
function RulesSheet({
  cfg,
  onClose,
}: {
  cfg: NonNullable<QualificationOverview['qualification']>;
  onClose: () => void;
}) {
  const rules: Array<{ chip: string; cls?: string; text: string }> = [
    { chip: String(cfg.startRating), text: `Все начинают отбор с ${cfg.startRating}. Ниже ${cfg.floorRating} опуститься нельзя.` },
    { chip: '⇅', text: 'В каждом турнире ты получаешь очки за каждого игрока, которого обошёл, и теряешь за тех, кто обошёл тебя. Обойти сильного соперника выгоднее.' },
    { chip: `+${cfg.bonus}`, cls: 'q-chip-green', text: `За каждый сыгранный турнир +${cfg.bonus}, независимо от результата.` },
    { chip: String(cfg.calGames), text: `Первые ${cfg.calGames} турнира — калибровка: рейтинг меняется быстрее, место в таблице появляется после ${cfg.calGames}-го турнира.` },
    { chip: `−${cfg.softStep}`, cls: 'q-chip-decay', text: `Один пропуск бесплатно. Со ${cfg.softFrom}-го пропуска подряд — −${cfg.softStep} за каждый, с ${cfg.hardFrom}-го подряд — −${cfg.hardStep}.` },
    { chip: String(cfg.tickets), text: `Топ-${cfg.tickets} по итогам ${cfg.weeks} недель едут на Первый Чемпионат России по спортивному покеру. Если кто-то не может поехать, место переходит следующему по таблице.` },
  ];
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet q-rules" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        <div className="q-rules-title">Как считается рейтинг</div>
        {rules.map((r, i) => (
          <div key={i} className="q-rule">
            <span className={`q-rule-chip mono ${r.cls ?? ''}`}>{r.chip}</span>
            <span className="q-rule-text">{r.text}</span>
          </div>
        ))}
        <button className="btn" style={{ width: '100%', marginTop: 14 }} onClick={onClose}>
          Понятно
        </button>
      </div>
    </div>
  );
}

/** Состояние «между отборами» / «отбор скоро»: анонс + архив. */
function BetweenState({ overview }: { overview: QualificationOverview | null }) {
  const archive = useQualificationArchive(true);
  const upcoming = overview?.status === 'upcoming' ? overview : null;
  return (
    <div className="q-screen">
      <div className="q-header q-header--between" data-theme="dark">
        <img src={logoUrl} alt="" className="q-header-wm" />
        <span className="q-header-label">
          <TicketIcon size={12} /> ОТБОР
        </span>
        {upcoming?.qualification ? (
          <>
            <div className="q-header-title">{upcoming.qualification.name}</div>
            <div className="q-between-count mono">
              старт через {upcoming.progress?.daysToStart ?? '—'} {plural(upcoming.progress?.daysToStart ?? 0, ['день', 'дня', 'дней'])}
            </div>
            <div className="q-between-sub">
              {upcoming.qualification.weeks} недель · план {upcoming.qualification.plannedGames} турниров · топ-
              {upcoming.qualification.tickets} едут на Чемпионат
            </div>
          </>
        ) : (
          <>
            <div className="q-header-title">Отбор на Чемпионат</div>
            <div className="q-between-sub">Новый отбор скоро объявят — следи за новостями клуба.</div>
          </>
        )}
      </div>

      {(archive.data?.length ?? 0) > 0 && (
        <>
          <div className="q-overline">ПРОШЛЫЕ ОТБОРЫ</div>
          {archive.data!.map((a) => (
            <div key={a.id} className="q-archive-card">
              <div className="q-archive-top">
                <span className="nm">{a.name}</span>
                <span className="dt mono">{shortDate(a.endedAt)}</span>
              </div>
              <div className="q-archive-winners">
                {a.winners.map((w) => (
                  <span key={w.rank} className="q-archive-winner">
                    <span className="mono">#{w.rank}</span> {w.name}
                  </span>
                ))}
              </div>
              {a.myResult && (
                <div className="q-archive-me mono">
                  Ты: {a.myResult.rank !== null ? `#${a.myResult.rank}` : 'калибровка'} · {a.myResult.rating} ·{' '}
                  {a.myResult.gamesPlayed} игр
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function QualSkeleton() {
  return (
    <div className="q-screen">
      <div className="skeleton" style={{ height: 170, borderRadius: 20, marginBottom: 12 }} />
      <div className="skeleton" style={{ height: 88, borderRadius: 18, marginBottom: 12 }} />
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 52, borderRadius: 12, marginBottom: 8 }} />
      ))}
    </div>
  );
}
